import React from "react";
import { Link } from "react-router-dom";
import PublicShell, { LegalSection } from "@/components/PublicShell";

const ENTITY = "Zealous FZE LLC";
const JURISDICTION = "the United Arab Emirates";

export default function Terms() {
  return (
    <PublicShell
      eyebrow="Legal"
      title="Terms of Service"
      intro="These terms govern your use of the Matiqs marketplace, quotation tools and e-invoicing service."
      updated="June 2026"
    >
      <LegalSection n={1} title="Who we are and what Matiqs is">
        <p>
          Matiqs (“Matiqs”, “we”, “us”) is operated by {ENTITY}, registered in {JURISDICTION}. Matiqs provides the
          Matiqs Qonnect UAE FTA e-invoicing document service for businesses of every sector, together with a vendor
          listing network and quotation-building tools for the contracting and procurement industry.
        </p>
        <p>
          <strong>Matiqs is not a party to any contract between a contractor and a vendor.</strong> We do not sell,
          supply, install or guarantee any materials or services, we do not act as agent or broker for either side, and
          we do not handle payments between users. Any purchase order, subcontract or settlement is strictly between the
          contractor and the vendor.
        </p>
      </LegalSection>

      <LegalSection n={2} title="Accounts">
        <p>
          Vendor accounts are for businesses. You must give accurate company information, keep your login credentials
          confidential and remain responsible for everything done under your account. You must be authorised to act for
          the company you register.
        </p>
        <p>
          Contractors can search the marketplace without an account. Where an account is offered, the same rules apply.
        </p>
      </LegalSection>

      <LegalSection n={3} title="Your data stays yours">
        <p>
          You retain all rights in the data you upload — your price list (catalogue), contractor BOQs, quotations, company
          profile, logo and documents. You grant us a limited licence to host and process that data solely to operate the
          service for you: matching BOQ lines to your catalogue, reading document expiry dates, generating your PDF and
          Excel outputs, and displaying the profile information you have chosen to publish.
        </p>
        <p>
          <strong>Vendor catalogues are private.</strong> Price lists and rates are never published on the marketplace,
          are not searchable by contractors, and are not shared with other vendors. Only the information on your public
          profile is visible, and you can switch public listing off at any time.
        </p>
        <p>We do not use your data to train AI models. See our <Link to="/privacy" className="text-primary font-semibold hover:underline">Privacy Policy</Link>.</p>
      </LegalSection>

      <LegalSection n={4} title="Quotations, rates and AI matching">
        <p>
          Automatic matching is an assistant, not a pricing authority. Matches are suggestions with a confidence level
          and may be wrong or incomplete. <strong>Every rate, quantity, exclusion and total in a quotation is the
          vendor's own commercial decision and responsibility.</strong> The vendor must review a quotation before issuing
          it.
        </p>
        <p>
          Matiqs gives no warranty that a generated quotation is accurate, complete, competitive or fit for a particular
          tender, and is not liable for pricing errors, omitted items, misread quantities or any loss arising from a
          quotation issued through the service.
        </p>
        <p>
          Contractors should treat every quotation as coming from the vendor, not from Matiqs, and should verify scope,
          rates and validity directly with the vendor.
        </p>
      </LegalSection>

      <LegalSection n={5} title="Verification badges and vendor documents">
        <p>
          Vendors may upload compliance documents such as a trade licence, VAT/TRN certificate, insurance policy or other
          certificates. Our AI reads each document to extract its expiry date, and the profile shows Valid, Expiring soon
          or Expired accordingly. Badges reflect what a vendor has uploaded and the dates we could read — they are not an
          audit, a credit check, or an endorsement of quality, capability or workmanship.
        </p>
        <p>
          Uploading false, altered or expired documents, or misrepresenting your trades, licences or capabilities, is a
          serious breach of these terms. <strong>We may suspend or terminate an account, and remove a listing, where we
          reasonably believe documents or profile information are false or misleading.</strong> Contractors remain
          responsible for their own due diligence and pre-qualification.
        </p>
      </LegalSection>

      <LegalSection n={6} title="Plans, fair use and payment">
        <p>
          Matiqs offers a Free plan (30 automatic quotes per calendar month) and paid plans with
          higher or unlimited allowances plus marketplace logo placement — see the pricing page for
          the current tiers and prices. An “automatic quote” is one BOQ upload processed by AI matching; editing, revising and
          exporting existing quotations is not counted.
        </p>
        <p>
          The e-invoicing service is priced <strong>separately from the marketplace plans</strong>, in AED. The Full
          Service plan — where Matiqs submits each invoice directly to the Approved Service Provider on your
          behalf — carries a one-time onboarding fee, an annual platform fee and a per-invoice rate, with no volume
          limit. Current amounts are as shown on the pricing and e-invoicing pages; usage is metered and shown against
          your active plan.
        </p>
        <p>
          Unlimited use is subject to fair use. We may apply reasonable rate limits or contact you where usage is
          automated, resold, or materially beyond normal business use. Paid plans renew for the period selected until
          cancelled. Fees are stated exclusive of taxes unless shown otherwise, and are non-refundable for a period
          already used except where the law requires otherwise. We may change pricing with reasonable notice.
        </p>
      </LegalSection>

      <LegalSection n={7} title="Acceptable use">
        <p>You agree not to:</p>
        <p>
          (a) scrape, harvest or bulk-collect vendor or contractor details from the marketplace; (b) use the platform to
          send spam or unsolicited marketing; (c) upload malware or content you have no right to upload; (d) attempt to
          access another user's account, catalogue, quotations or documents; (e) reverse engineer or resell the service;
          or (f) use the service for any unlawful purpose, including sanctions or anti-bribery breaches.
        </p>
      </LegalSection>

      <LegalSection n={8} title="Third-party and online search results">
        <p>
          Where no listed vendor matches a search, we may show companies found online. Those results are clearly marked
          as unverified, are not Matiqs vendors, and are provided for convenience only. We do not endorse them and are
          not responsible for their content, availability or conduct.
        </p>
      </LegalSection>

      <LegalSection n={9} title="Availability and changes">
        <p>
          We aim to keep Matiqs available but do not guarantee uninterrupted service. We may add, change or withdraw
          features, and may perform maintenance. We may update these terms; material changes will be notified through the
          service or by email, and continued use after the effective date means you accept them.
        </p>
      </LegalSection>

      <LegalSection n={10} title="Limitation of liability">
        <p>
          To the fullest extent permitted by law, the service is provided “as is” and “as available” without warranties
          of any kind. Matiqs and {ENTITY} are not liable for indirect, incidental, special or consequential loss, nor
          for loss of profit, revenue, contracts, tenders, goodwill, data or anticipated savings, however caused.
        </p>
        <p>
          Our total aggregate liability arising out of or relating to the service is limited to the greater of (a) the
          fees you paid us in the twelve months before the event giving rise to the claim, or (b) USD 100. Nothing in
          these terms excludes liability that cannot be excluded by law.
        </p>
        <p>
          You agree to indemnify us against claims arising from your content, your quotations, your documents or your
          dealings with another user.
        </p>
      </LegalSection>

      <LegalSection n={11} title="Suspension and termination">
        <p>
          You may stop using Matiqs and ask us to close your account at any time. We may suspend or terminate access for
          breach of these terms, non-payment, or where required by law. On termination we will delete or de-identify your
          data in line with our Privacy Policy, subject to any legal retention obligation. Export your catalogue and
          quotations before you close your account.
        </p>
      </LegalSection>

      <LegalSection n={12} title="E-invoicing service (UAE FTA)">
        <p>
          The Matiqs Qonnect e-invoicing service generates tax-invoice documents from the contract and progress information you
          upload, in formats aligned to the UAE FTA e-invoicing framework (Peppol PINT-AE / UBL 2.1), and exports them as
          Excel, CSV, PDF and optional UBL XML. <strong>Matiqs Qonnect is a lightweight ERP / ERP alternative and a
          document-generation tool; it acts as Corner 1 and does not itself transmit invoices to the FTA.</strong> Matiqs Qonnect partners
          with Accredited Service Providers (ASPs) who perform the accredited transmission — partnerships
          intended to make compliant e-invoicing more affordable for SMBs and SMEs. A
          connected submission option through an Approved ASP is planned but not yet available. Until
          it launches, you are responsible for submitting the generated files to your ASP and for the clearance process.
        </p>
        <p>
          You are solely responsible for the accuracy and legality of every invoice you generate and issue — including
          the contract BOQ, certified quantities, VAT treatment, TRN and buyer details, advance recovery and retention.
          Matiqs Qonnect performs automated reconciliation and a lightweight pre-check to help you catch errors, but these are
          aids only and are not tax, accounting or legal advice. We give no warranty that a generated document will be
          accepted by any ASP or authority, and we are not liable for rejected filings, penalties, interest or any loss
          arising from invoices produced through the service. Where a connected-ASP submission through an Approved ASP partner
          is offered, Matiqs Qonnect merely relays your files to that ASP and is not responsible for the ASP's acts,
          availability or clearance decisions.
        </p>
      </LegalSection>

      <LegalSection n={13} title="Governing law and contact">
        <p>
          These terms are governed by the laws of {JURISDICTION}, and the courts of {JURISDICTION} have exclusive
          jurisdiction over any dispute, subject to any mandatory consumer protections.
        </p>
        <p>
          Legal notices and questions: <a href="mailto:admin@matiqs.net" className="text-brand font-semibold hover:underline">admin@matiqs.net</a>.
        </p>
      </LegalSection>
    </PublicShell>
  );
}
